// Story 7-FU-I (ADO-1222) — the depot-scoped READ of the recommendations cache.
//
// `useRecommendationsStore` is depot-aware on the WRITE side (it stamps the fetched depot on
// each entry and `load()` refetches when the active depot differs), but every consumer read
// `bySku[sku]` directly, so between a live switch and the refetch landing they all served the
// PREVIOUS depot's rails. These two readers are the only supported way in; they apply
// `atDepot` so the compare cannot be forgotten at a call site.
//
// Rails are depot-scoped because `fetchRecommendations` is: a product linked/co-purchased at
// one depot is not necessarily either at another, and the tiles carry Add buttons gated on
// depot-scoped availability — so acting on the old depot's list is a real action on wrong data.

import { atDepot } from '../availability/depot-scoped-entry';
import { resolveSessionDepotId, useSessionDepotId } from '../availability/session-depot';
import { useRecommendationsStore, type RecommendationsEntry } from './useRecommendationsStore';

/**
 * `sku`'s recommendations entry IF it belongs to the current session depot, else `undefined`
 * (treated as not-yet-loaded — the consumer renders its empty/pending state through the
 * refetch). Subscribes to the depot, so a live switch re-renders the caller.
 *
 * `sku === null` (nothing selected) yields `undefined` without subscribing to an entry.
 */
export function useRecommendationsEntry(sku: string | null): RecommendationsEntry | undefined {
  const entry = useRecommendationsStore((s) => (sku === null ? undefined : s.bySku[sku]));
  const sessionDepotId = useSessionDepotId();
  return atDepot(entry, sessionDepotId);
}

/**
 * The same read for callers OUTSIDE React (imperative store actions). Resolves the session
 * depot at call time — so a switch that lands while the caller was awaiting `load()` yields
 * `undefined` rather than the depot it started at.
 */
export function recommendationsEntryAtSessionDepot(sku: string): RecommendationsEntry | undefined {
  return atDepot(useRecommendationsStore.getState().bySku[sku], resolveSessionDepotId());
}
